import React, { useState, Children, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, AlertTriangleIcon, ClockIcon, AwardIcon, HeartIcon, TrendingUpIcon, ChevronDownIcon, FileTextIcon } from 'lucide-react';
import { Badge } from '../ui/Shared';
const MODULES = [{
  id: 'm1',
  num: 'M1',
  title: 'Conocé a tu alumno antes de enseñar',
  sub: 'Diagnóstico en la primera clase, detección del perfil real y construcción del plan personalizado. El error más caro que comete el profesor es enseñar sin este paso.',
  tags: ['Diagnóstico inicial', 'Test de nivel', 'Plan personalizado', 'Checklist primera clase'],
  pages: '50 págs.',
  profile: 'Todos los perfiles',
  icon: SearchIcon
}, {
  id: 'm2',
  num: 'M2',
  title: 'El itañol: el obstáculo invisible',
  sub: 'Los 20 errores más frecuentes del rioplatense en italiano. Cómo trabajar el dialecto heredado sin humillar al alumno. Tablas, drills y protocolo de corrección en tiempo real.',
  tags: ['Tabla de 20 errores', 'Falsos cognados', 'Dialectalismos', 'Protocolo de corrección'],
  pages: '50 págs.',
  profile: 'Todos los perfiles',
  icon: AlertTriangleIcon
}, {
  id: 'm3',
  num: 'M3',
  title: 'El alumno de ciudadanía: urgente y rentable',
  sub: 'El perfil de mayor demanda en Argentina hoy. Vocabulario consular A2, plan intensivo de 4 semanas, preguntas reales del cónsul y cómo cobrar por programa completo.',
  tags: ['150 palabras consulares', 'Plan 4 semanas', 'Simulacro oral', 'Por consulado'],
  pages: '60 págs.',
  profile: 'Perfil ciudadanía',
  icon: ClockIcon
}, {
  id: 'm4',
  num: 'M4',
  title: 'Certificaciones CILS y CELI',
  sub: 'Estructura exacta de ambos exámenes, criterios reales del examinador, planes de 12 y 16 semanas, rúbricas de corrección y simulacros por nivel.',
  tags: ['CILS vs CELI', 'Rúbricas del examinador', 'Simulacros B1 y B2', 'Cronogramas'],
  pages: '60 págs.',
  profile: 'Perfil certificación',
  icon: AwardIcon
}, {
  id: 'm5',
  num: 'M5',
  title: 'El alumno que no abandona',
  sub: 'Los 3 momentos críticos donde el adulto decide irse y qué hace el profesor en cada uno. Semáforo de retención, mensajes de WhatsApp listos y guiones de renovación.',
  tags: ['Semáforo retención', '10 mensajes WhatsApp', 'Guiones de renovación', 'Mapa de progreso'],
  pages: '50 págs.',
  profile: 'Todos los perfiles',
  icon: HeartIcon
}, {
  id: 'm6',
  num: 'M6',
  title: 'De hora vendida a sistema de ingresos',
  sub: 'Calculadora de tarifa real, segmentación por perfil, cómo armar programas cerrados, cobrar en dólares y crear tu primer producto digital en una semana.',
  tags: ['Calculadora de tarifa', 'Programas cerrados', 'Cobro en dólares', 'Producto digital'],
  pages: '50 págs.',
  profile: 'Negocio docente',
  icon: TrendingUpIcon
}];
const tagContainer = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.1
    }
  }
};
const tagItem = {
  hidden: {
    opacity: 0,
    y: 6,
    scale: 0.95
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2
    }
  }
};
export const Modules = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toggleModule = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };
  return <section className="py-20 px-5 pt-[30px] pb-[30px] pl-[20px] pr-[20px]" data-id="element-134">
      <div className="max-w-[860px] mx-auto" data-id="element-135">
        <div className="text-center mb-4" data-id="element-136">
          <Badge color="cyan" data-id="element-137">Sistema N°1 en Argentina</Badge>
        </div>

        {/* Infodigital Core logo */}
        <div className="flex justify-center mb-4 md:mb-6" data-id="element-138">
          <img src="https://cdn.magicpatterns.com/uploads/odZXQDe2dK6UB2r8dxiY5J/logo_(2).png" alt="Infodigital Core" width="240" height="240" loading="lazy" decoding="async" className="w-[200px] md:w-[240px] h-auto select-none pointer-events-none" data-id="element-139" />
        </div>

        <div className="text-center mb-12" data-id="element-140">
          <h2 className="text-[clamp(26px,4vw,36px)] font-extrabold leading-[1.2] mb-3" data-id="element-141">
            KIT ITALIANO PRO™
          </h2>
          <p className="text-[16px] text-[#aaa] max-w-[580px] mx-auto leading-relaxed" data-id="element-142">
            Sistema completo · 6 Módulos + 6 Bonos · Todos los niveles · Todo
            resuelto.
          </p>

          <div className="flex flex-wrap justify-center gap-2.5 mt-5" data-id="element-143">
            <Badge color="cyan" data-id="element-144">+570 págs.</Badge>
            <Badge color="cyan" data-id="element-145">36 herramientas</Badge>
            <Badge color="cyan" data-id="element-146">3 perfiles de alumno</Badge>
            <Badge color="cyan" data-id="element-147">Descarga y aplica</Badge>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mt-3.5" data-id="element-148">
            {['Ciudadanía', 'CILS / CELI', 'Diagnóstico', 'Retención', 'Ingresos', 'Itañol', 'Metodología', 'Planificación'].map((tag, i) => <span key={i} className="text-[12px] text-[#888] px-2.5 py-1 bg-[#1a1a1a] rounded-full" data-id="element-149">
                {tag}
              </span>)}
          </div>
        </div>

        <div className="text-center mb-7" data-id="element-150">
          <p className="text-xs tracking-[0.1em] uppercase text-cyan mb-3 font-semibold" data-id="element-151">
            Los 6 módulos
          </p>
          <h3 className="text-[20px] md:text-[22px] font-bold text-blanco" data-id="element-152">
            Tu hoja de ruta para dominar la docencia de italiano
          </h3>
          <p className="text-[12px] text-gris-claro mt-2" data-id="element-153">
            Tocá un módulo para ver el detalle
          </p>
        </div>

        <div className="flex flex-col gap-2 md:gap-2.5" data-id="element-154">
          {MODULES.map((mod, idx) => {
          const isExpanded = expandedId === mod.id;
          const Icon = mod.icon;
          return <motion.div key={mod.id} className={`relative bg-gris-oscuro border rounded-xl overflow-hidden cursor-pointer transition-all duration-200 ${isExpanded ? 'border-cyan/50 shadow-[0_0_24px_rgba(0,229,255,0.08)]' : 'border-gris-medio hover:border-cyan/30 hover:bg-[#1c1c1c]'}`} onClick={() => toggleModule(mod.id)} layout data-id="element-155">
                {/* Left accent bar */}
                <AnimatePresence data-id="element-156">
                  {isExpanded && <motion.div initial={{
                scaleY: 0
              }} animate={{
                scaleY: 1
              }} exit={{
                scaleY: 0
              }} transition={{
                duration: 0.25,
                ease: 'easeOut'
              }} style={{
                originY: 0.5
              }} className="absolute left-0 top-0 bottom-0 w-[3px] bg-cyan shadow-[0_0_12px_rgba(0,229,255,0.6)]" data-id="element-157" />}
                </AnimatePresence>

                {/* Compact row */}
                <div className="relative flex items-center gap-3 px-3 py-2.5 md:px-5 md:py-4" data-id="element-158">
                  {/* Icon + module number combined badge */}
                  <div className={`relative flex items-center justify-center shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-lg border transition-all duration-300 ${isExpanded ? 'bg-cyan/15 border-cyan/40 text-cyan shadow-[0_0_16px_rgba(0,229,255,0.25)]' : 'bg-[#222] border-[#333] text-gris-claro'}`} data-id="element-159">
                    <Icon size={18} strokeWidth={2.2} data-id="element-160" />
                    <span className={`absolute -bottom-1 -right-1 text-[9px] font-bold tracking-wider px-1 py-px rounded-sm transition-colors ${isExpanded ? 'bg-cyan text-black' : 'bg-[#0d0d0d] text-cyan border border-[#333]'}`} data-id="element-161">
                      {mod.num}
                    </span>
                  </div>

                  {/* Title + meta */}
                  <div className="flex-1 min-w-0 pr-1" data-id="element-162">
                    <h4 className={`text-[13.5px] md:text-[15px] font-bold leading-[1.3] transition-colors ${isExpanded ? 'text-cyan' : 'text-blanco'}`} data-id="element-163">
                      {mod.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-0.5" data-id="element-164">
                      <span className="text-[10.5px] text-gris-claro flex items-center gap-1" data-id="element-165">
                        <FileTextIcon size={10} data-id="element-166" /> {mod.pages}
                      </span>
                      <span className="text-[10.5px] text-gris-claro/70 hidden sm:inline" data-id="element-167">
                        · {mod.profile}
                      </span>
                    </div>
                  </div>

                  {/* Chevron */}
                  <motion.div animate={{
                rotate: isExpanded ? 180 : 0
              }} transition={{
                duration: 0.25
              }} className={`shrink-0 transition-colors ${isExpanded ? 'text-cyan' : 'text-gris-claro'}`} data-id="element-168">
                    <ChevronDownIcon size={18} data-id="element-169" />
                  </motion.div>
                </div>

                {/* Expanded content */}
                <AnimatePresence initial={false} data-id="element-170">
                  {isExpanded && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-171">
                      <div className="px-3 pb-4 md:px-5 md:pb-5 pt-0" data-id="element-172">
                        <div className="border-t border-cyan/10 pt-3.5 md:pt-4" data-id="element-173">
                          <p className="text-[13px] md:text-[14px] text-[#bbb] leading-relaxed mb-3.5" data-id="element-174">
                            {mod.sub}
                          </p>

                          <motion.div variants={tagContainer} initial="hidden" animate="show" className="flex flex-wrap gap-1.5" data-id="element-175">
                            {mod.tags.map((tag, i) => <motion.span variants={tagItem} key={i} className="text-[10.5px] md:text-[11px] px-2.5 py-1 rounded-full bg-cyan/[0.06] text-cyan/80 border border-cyan/15 font-medium" data-id="element-176">
                                {tag}
                              </motion.span>)}
                          </motion.div>

                          <div className="flex items-center justify-between mt-3.5 pt-3 border-t border-gris-medio/40" data-id="element-177">
                            <span className="text-[10.5px] text-cyan/60 font-medium tracking-wide uppercase" data-id="element-178">
                              {mod.profile}
                            </span>
                            <span className="text-[10.5px] text-gris-claro/60" data-id="element-179">
                              Módulo {idx + 1} de {MODULES.length}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
};